/**
 * Patients Page
 *
 * List, search and manage patients
 */

import { useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Users,
  Search,
  Plus,
  Eye,
  Edit,
  Trash2,
  FileImage,
  Loader2,
  AlertCircle,
} from 'lucide-react'

import { usePatients, useDeletePatient } from '@/application/patients'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import { ConfirmDialog } from '@/presentation/components/ui/ConfirmDialog'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/presentation/components/ui/Table'
import { PaginationBar } from '@/presentation/components/ui/Pagination'
import {
  DOCUMENT_TYPE_LABELS,
  PAGINATION,
  type DocumentType,
} from '@/config/constants'
import type { Patient } from '@/domain/patient/types'
import { aFechaSegura } from '@/lib/utils'

// =============================================================================
// Constants
// =============================================================================

const DOCUMENT_TYPE_OPTIONS = Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}))

const SEX_LABELS: Record<string, string> = {
  M: 'Masculino',
  F: 'Femenino',
  O: 'Otro',
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatDate(dateString: string): string {
  return aFechaSegura(dateString).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function calculateAge(birthDate: string): number {
  const today = new Date()
  const birth = aFechaSegura(birthDate)
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return age
}

// =============================================================================
// Components
// =============================================================================

interface PatientRowProps {
  patient: Patient
  onDelete: (id: string) => void
  isDeleting: boolean
}

function PatientRow({ patient, onDelete, isDeleting }: PatientRowProps) {
  const age = calculateAge(patient.birthDate)

  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium">
            {patient.firstName} {patient.lastName}
          </span>
          <span className="text-xs text-[var(--foreground-muted)]">
            {DOCUMENT_TYPE_LABELS[patient.document.type as DocumentType]} {patient.document.number}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="secondary">{SEX_LABELS[patient.sex]}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span>{formatDate(patient.birthDate)}</span>
          <span className="text-xs text-[var(--foreground-muted)]">{age} años</span>
        </div>
      </TableCell>
      <TableCell>
        {patient.contact?.phone || <span className="text-[var(--foreground-muted)]">-</span>}
      </TableCell>
      <TableCell>
        {patient.contact?.city || <span className="text-[var(--foreground-muted)]">-</span>}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Link to={`/patients/${patient.id}`}>
            <Button variant="ghost" size="sm" title="Ver detalles">
              <Eye className="h-4 w-4" />
            </Button>
          </Link>
          <Link to={`/studies?patientId=${patient.id}`}>
            <Button variant="ghost" size="sm" title="Ver estudios">
              <FileImage className="h-4 w-4" />
            </Button>
          </Link>
          <Link to={`/patients/${patient.id}/edit`}>
            <Button variant="ghost" size="sm" title="Editar">
              <Edit className="h-4 w-4" />
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(patient.id)}
            disabled={isDeleting}
            title="Eliminar"
            className="text-[var(--destructive)] hover:text-[var(--destructive)]"
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Users className="h-12 w-12 text-[var(--foreground-muted)] mb-4" />
      <h3 className="text-lg font-semibold text-[var(--foreground)]">
        No se encontraron pacientes
      </h3>
      <p className="text-[var(--foreground-muted)] mt-1">
        Intenta ajustar los filtros de búsqueda o crea un nuevo paciente
      </p>
      <Link to="/patients/new" className="mt-4">
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Paciente
        </Button>
      </Link>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      <p className="text-[var(--foreground-muted)] mt-4">Cargando pacientes...</p>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="h-12 w-12 text-[var(--destructive)] mb-4" />
      <h3 className="text-lg font-semibold text-[var(--foreground)]">Error al cargar</h3>
      <p className="text-[var(--foreground-muted)] mt-1">{message}</p>
      <Button onClick={onRetry} className="mt-4">
        Reintentar
      </Button>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function PatientsPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Filters from URL
  const documentType = searchParams.get('documentType') || ''
  const documentNumber = searchParams.get('documentNumber') || ''
  const name = searchParams.get('name') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)
  const pageSize = PAGINATION.DEFAULT_LIMIT

  // Local state for form inputs
  const [localDocumentType, setLocalDocumentType] = useState(documentType)
  const [localDocumentNumber, setLocalDocumentNumber] = useState(documentNumber)
  const [localName, setLocalName] = useState(name)

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [patientToDelete, setPatientToDelete] = useState<string | null>(null)

  // Build filters object
  const filters = useMemo(
    () => ({
      documentType: (documentType || undefined) as DocumentType | undefined,
      documentNumber: documentNumber || undefined,
      name: name || undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [documentType, documentNumber, name, page, pageSize]
  )

  // Queries
  const { data, isLoading, isError, error, refetch } = usePatients(filters)
  const deletePatientMutation = useDeletePatient()

  // Calculated values
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams()
    if (localDocumentType) params.set('documentType', localDocumentType)
    if (localDocumentNumber) params.set('documentNumber', localDocumentNumber)
    if (localName) params.set('name', localName)
    params.set('page', '1')
    setSearchParams(params)
  }

  const handleClearFilters = () => {
    setLocalDocumentType('')
    setLocalDocumentNumber('')
    setLocalName('')
    setSearchParams({ page: '1' })
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  const handleDelete = (id: string) => {
    setPatientToDelete(id)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (!patientToDelete) return
    deletePatientMutation.mutate(patientToDelete, {
      onSuccess: () => {
        toast.success('Paciente eliminado')
        setDeleteDialogOpen(false)
        setPatientToDelete(null)
      },
      onError: (error) => {
        toast.error(getErrorMessage(error, 'Error al eliminar paciente'))
        setDeleteDialogOpen(false)
      },
    })
  }

  const hasFilters = documentType || documentNumber || name

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">Pacientes</h1>
          <p className="text-[var(--foreground-muted)]">
            Gestiona la información de los pacientes
          </p>
        </div>
        <Link to="/patients/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Paciente
          </Button>
        </Link>
      </div>

      {/* Search Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar por nombre..."
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>
            <div className="w-[180px]">
              <Select
                options={DOCUMENT_TYPE_OPTIONS}
                value={localDocumentType}
                onChange={(e) => setLocalDocumentType(e.target.value)}
                placeholder="Tipo documento"
              />
            </div>
            <div className="w-[180px]">
              <Input
                placeholder="Nº Documento"
                value={localDocumentNumber}
                onChange={(e) => setLocalDocumentNumber(e.target.value)}
              />
            </div>
            <Button type="submit">
              <Search className="h-4 w-4 mr-2" />
              Buscar
            </Button>
            {hasFilters && (
              <Button type="button" variant="outline" onClick={handleClearFilters}>
                Limpiar
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <ErrorState
              message={error?.message || 'Error desconocido'}
              onRetry={() => refetch()}
            />
          ) : !data?.items.length ? (
            <EmptyState />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Paciente</TableHead>
                    <TableHead>Género</TableHead>
                    <TableHead>Fecha Nacimiento</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Ciudad</TableHead>
                    <TableHead className="w-[150px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((patient) => (
                    <PatientRow
                      key={patient.id}
                      patient={patient}
                      onDelete={handleDelete}
                      isDeleting={deletePatientMutation.isPending}
                    />
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <PaginationBar
                  currentPage={page}
                  pageSize={pageSize}
                  total={data.total}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Eliminar paciente"
        description="¿Estás seguro de que deseas eliminar este paciente? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmDelete}
        isLoading={deletePatientMutation.isPending}
      />
    </div>
  )
}
